import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useCreateList, useLists } from '../../api/hooks';
import type { ListSummary } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { displayName } from '../../lib/next';
import { Button, IconButton } from '../../ui/Button';
import { Field } from '../../ui/Field';
import { IconCart, IconChevronRight, IconLogout, IconPlus, IconUsers, LogoMark } from '../../ui/icons';
import { Badge, EmptyState, Skeleton } from '../../ui/Misc';
import { Sheet } from '../../ui/Sheet';
import { Page, ThemeToggle, TopBar } from '../../ui/TopBar';

export function ListsScreen() {
  const { user, logout } = useAuth();
  const lists = useLists();
  const [creating, setCreating] = useState(false);

  const data = lists.data ?? [];

  return (
    <div className="min-h-dvh pb-[calc(var(--safe-bottom)+6rem)]">
      <TopBar
        title={
          <span className="flex items-center gap-2.5">
            <LogoMark size={28} />
            <span className="text-lg font-bold tracking-tight">Cady</span>
          </span>
        }
        right={
          <>
            <ThemeToggle />
            <IconButton label="Sign out" onClick={() => void logout()}>
              <IconLogout />
            </IconButton>
          </>
        }
      />
      <Page>
        <div className="flex items-end justify-between pt-6 pb-4">
          <div>
            <h1 className="text-[1.75rem] leading-tight font-bold tracking-tight">Your lists</h1>
            {user && <p className="mt-0.5 text-[0.95rem] text-muted">Hi, {displayName(user)}</p>}
          </div>
          {data.length > 0 && (
            <p className="text-sm text-muted">
              {data.length} list{data.length === 1 ? '' : 's'}
            </p>
          )}
        </div>

        {lists.isPending ? (
          <div className="space-y-3" aria-busy="true" aria-label="Loading lists">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-[5.5rem] rounded-2xl" />
            ))}
          </div>
        ) : lists.isError && !lists.data ? (
          <EmptyState
            icon={<IconCart size={30} />}
            title="Couldn't load your lists"
            action={
              <Button variant="secondary" onClick={() => void lists.refetch()} loading={lists.isFetching}>
                Try again
              </Button>
            }
          >
            {lists.error.message}
          </EmptyState>
        ) : data.length === 0 ? (
          <EmptyState
            icon={<IconCart size={30} />}
            title="No lists yet"
            action={
              <Button variant="primary" size="lg" onClick={() => setCreating(true)}>
                <IconPlus size={20} /> Create your first list
              </Button>
            }
          >
            Make a list for groceries, the hardware store, or this weekend’s party, then share it with your family.
          </EmptyState>
        ) : (
          <ul className="space-y-3" aria-label="Lists">
            {data.map((l) => (
              <li key={l.id}>
                <ListCard list={l} />
              </li>
            ))}
          </ul>
        )}
      </Page>

      {data.length > 0 && (
        <div className="pointer-events-none fixed inset-x-0 bottom-0 z-20 pb-[calc(var(--safe-bottom)+1.25rem)]">
          <div className="mx-auto flex max-w-xl justify-end px-5">
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="pointer-events-auto inline-flex min-h-14 items-center gap-2 rounded-full bg-accent pr-6 pl-5 text-base font-semibold text-on-accent shadow-lg transition-transform active:scale-95"
            >
              <IconPlus /> New list
            </button>
          </div>
        </div>
      )}

      <CreateListSheet open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

function ListCard({ list }: { list: ListSummary }) {
  const pct = list.itemCount ? Math.round((list.doneCount / list.itemCount) * 100) : 0;
  const remaining = list.itemCount - list.doneCount;
  return (
    <Link
      to={`/lists/${list.id}`}
      className="group block rounded-2xl border border-line bg-surface p-4 shadow-sm transition-[transform,box-shadow] hover:shadow-md active:scale-[0.99]"
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="min-w-0 text-[1.0625rem] font-semibold break-words text-ink">{list.title}</h2>
            {list.role === 'member' ? (
              <Badge tone="accent">
                <IconUsers size={13} /> Shared with you
              </Badge>
            ) : (
              <Badge>Owner</Badge>
            )}
          </div>
          <p className="mt-1 text-sm text-muted">
            {list.itemCount === 0
              ? 'No items yet'
              : remaining === 0
                ? `All ${list.itemCount} done`
                : `${remaining} to get · ${list.doneCount} of ${list.itemCount} done`}
          </p>
        </div>
        <IconChevronRight className="mt-0.5 shrink-0 text-subtle transition-transform group-hover:translate-x-0.5" />
      </div>
      {list.itemCount > 0 && (
        <div
          className="mt-3 h-1.5 overflow-hidden rounded-full bg-surface-2"
          role="progressbar"
          aria-label={`${list.title} progress`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
        >
          <div className="h-full rounded-full bg-accent transition-[width] duration-300" style={{ width: `${pct}%` }} />
        </div>
      )}
    </Link>
  );
}

function CreateListSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const create = useCreateList();
  const navigate = useNavigate();

  function close() {
    setTitle('');
    setError(null);
    onClose();
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const t = title.trim();
    if (!t) return setError('Give your list a name.');
    if (t.length > 100) return setError('Keep it under 100 characters.');
    setError(null);
    create.mutate(t, {
      onSuccess: (list) => {
        close();
        navigate(`/lists/${list.id}`);
      },
    });
  }

  return (
    <Sheet open={open} onClose={close} title="New list">
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        <Field
          data-autofocus
          label="List name"
          name="title"
          placeholder="e.g. Groceries"
          autoComplete="off"
          enterKeyHint="done"
          maxLength={100}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          error={error}
        />
        <Button type="submit" size="lg" block loading={create.isPending}>
          Create list
        </Button>
      </form>
    </Sheet>
  );
}
